import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { categoriesService } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CategoriesTable = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    description: ''
  });

  useEffect(() => {
    loadCategories();
  }, [pagination.page, pagination.limit, filters]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = await categoriesService.getAllCategories(params);

      if (response.success) {
        setCategories(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Kategoriler yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Kategoriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setEditForm({
      name: category.name || '',
      description: category.description || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedCategory) return;

    const response = await categoriesService.updateCategory(selectedCategory.id, editForm);
    if (response.success) {
      toast.success('Kategori güncellendi');
      setShowEditModal(false);
      loadCategories();
    } else {
      toast.error(response.error || 'Kategori güncellenemedi');
    }
  };

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategory) return;

    const response = await categoriesService.deleteCategory(selectedCategory.id);
    if (response.success) {
      toast.success('Kategori silindi');
      setShowDeleteModal(false);
      loadCategories();
    } else {
      toast.error(response.error || 'Kategori silinemedi');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-0 pb-0 p-24">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <h5 className="mb-0">Kategoriler</h5>
            <form className="d-flex align-items-center gap-2">
              <div className="form-group mb-0">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ara..."
                  value={filters.search}
                  onChange={handleSearch}
                />
              </div>
              <select
                className="form-select"
                value={pagination.limit}
                onChange={handlePageSizeChange}
                style={{ width: 'auto' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </form>
          </div>
        </div>
        <div className="card-body p-24">
          {loading ? (
            <div className="text-center py-5">
              <p className="text-secondary-light">Yükleniyor...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-secondary-light">Kategori bulunamadı</p>
            </div>
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">S.L</th>
                      <th scope="col">Kategori Adı</th>
                      <th scope="col">Açıklama</th>
                      <th scope="col">Organizasyon Sayısı</th>
                      <th scope="col">Oluşturulma Tarihi</th>
                      <th scope="col" className="text-center">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category, index) => (
                      <tr key={category.id}>
                        <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                        <td>
                          <span className="text-md mb-0 fw-medium">
                            {category.name || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {category.description || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-primary">
                            {category.organization_count || 0}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {formatDate(category.created_at)}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center">
                            <button
                              type="button"
                              className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(category)}
                              title="Düzenle"
                            >
                              <Icon icon="lucide:edit" className="icon text-lg" />
                            </button>
                            <button
                              type="button"
                              className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleDelete(category)}
                              title="Sil"
                            >
                              <Icon icon="lucide:trash-2" className="icon text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="d-flex align-items-center justify-content-between mt-24">
                  <div className="text-secondary-light">
                    Toplam {pagination.total} kategori, Sayfa {pagination.page} / {pagination.totalPages}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      Önceki
                    </button>
                    {[...Array(pagination.totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= pagination.page - 2 && page <= pagination.page + 2)
                      ) {
                        return (
                          <button
                            key={page}
                            className={`btn btn-sm ${pagination.page === page ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === pagination.page - 3 ||
                        page === pagination.page + 3
                      ) {
                        return <span key={page} className="px-2">...</span>;
                      }
                      return null;
                    })}
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Kategori Düzenle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="form-group mb-3">
                  <label className="form-label">Kategori Adı <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Kategori adı"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="form-label">Açıklama</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Kategori açıklaması"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={!editForm.name.trim()}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedCategory && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Kategori Sil</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>{selectedCategory.name}</strong> kategorisini silmek istediğinize emin misiniz?
                </p>
                {selectedCategory.organization_count > 0 && (
                  <div className="alert alert-warning">
                    Bu kategori {selectedCategory.organization_count} organizasyon tarafından kullanılıyor. 
                    Önce organizasyonlardan kaldırılmalı.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={selectedCategory.organization_count > 0}
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoriesTable;

